import Hero from "@/components/Hero";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Dumbbell, Award, Salad, Globe, MessageCircle, HelpCircle, MapPin } from "lucide-react";

const QUICK_LINKS = [
  { title: "About Me", desc: "18+ years transforming lives", href: "/about", icon: Dumbbell },
  { title: "Credentials", desc: "Qualifications & certifications", href: "/qualifications", icon: Award },
  { title: "Personal Training", desc: "7 specializations tailored to you", href: "/personal-training", icon: Dumbbell },
  { title: "Nutrition", desc: "Science-based diet planning", href: "/nutrition", icon: Salad },
  { title: "Online Training", desc: "Train from anywhere", href: "/online-training", icon: Globe },
  { title: "FAQ", desc: "Common fitness myths answered", href: "/faq", icon: HelpCircle },
];

function CTABanner() {
  return (
    <section className="py-10 md:py-12" data-testid="cta-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gg-surface rounded-2xl p-6 md:p-8 border border-white/5 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <div>
            <h3 className="font-heading text-xl md:text-2xl font-extrabold uppercase tracking-tight text-white">
              Ready to start your <span className="text-gg-red">fitness journey?</span>
            </h3>
            <p className="text-zinc-500 text-sm mt-2 tracking-wide">
              Let's discuss your goals. First consultation is free.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a
              href="https://wa.me/919153020904?text=Hi!%20I'd%20like%20to%20book%20a%20free%20consultation."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-button"
              className="inline-flex items-center gap-2 bg-gg-red hover:bg-gg-red-hover text-white px-5 py-3 rounded-xl font-heading text-xs uppercase tracking-widest transition-all duration-300 hover:shadow-xl hover:shadow-gg-red/30 hover:scale-[1.03] btn-press group"
            >
              <MessageCircle size={14} />
              Book Free Consultation
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="tel:+919153020904"
              data-testid="cta-call-button"
              className="inline-flex items-center gap-2 border border-white/10 text-white px-5 py-3 rounded-xl font-heading text-xs uppercase tracking-widest hover:border-gg-red hover:text-gg-red transition-all duration-300"
            >
              <Phone size={14} />
              Call Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <CTABanner />

      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6">
            <span className="font-heading text-gg-red text-xs uppercase tracking-[0.4em]">Explore</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mt-2">What I Offer</h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
            className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          >
            {QUICK_LINKS.map((item, i) => (
              <motion.div key={i} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                <Link
                  to={item.href}
                  data-testid={`home-link-${i}`}
                  className="block border border-white/5 bg-gg-surface rounded-xl p-5 md:p-6 h-full card-lift btn-press transition-all duration-300 group relative overflow-hidden"
                >
                  <motion.div whileHover={{ rotate: 6, scale: 1.15 }} transition={{ type: "spring", stiffness: 300 }}>
                    <item.icon size={20} className="text-gg-red mb-3" />
                  </motion.div>
                  <h4 className="font-heading text-sm uppercase tracking-wider font-bold text-white group-hover:text-gg-red transition-colors duration-300">
                    {item.title}
                  </h4>
                  <p className="text-zinc-600 text-xs mt-1.5 leading-relaxed group-hover:text-zinc-400 transition-colors">{item.desc}</p>
                  <ArrowRight size={14} className="text-zinc-700 mt-3 group-hover:text-gg-red group-hover:translate-x-2 transition-all duration-300" />
                  <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gg-red group-hover:w-full transition-all duration-500 rounded-full" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Local SEO Section */}
      <section className="py-10 md:py-14 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center">
            <h2 className="font-heading text-xl sm:text-2xl font-extrabold uppercase tracking-tight">
              Your Trusted Fitness Partner in <span className="text-gg-red">Bolpur, Santiniketan</span>
            </h2>
            <p className="text-zinc-500 text-sm mt-4 max-w-2xl mx-auto leading-relaxed">
              Looking for a certified personal trainer near Bolpur or Santiniketan? GG Fitness offers professional fitness training right here in West Bengal. With over 18 years of experience, internationally certified credentials from K11, REPS India, and Kyokushin-kan Japan, Gora Ganguly provides personalized weight training, yoga, karate, nutrition planning, and online coaching for clients across India.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {[
                { label: "Personal Trainer Bolpur", href: "/personal-training" },
                { label: "Gym Trainer Santiniketan", href: "/personal-training" },
                { label: "Fitness Coach West Bengal", href: "/about" },
                { label: "Online Training India", href: "/online-training" },
                { label: "Yoga Classes Bolpur", href: "/personal-training" },
                { label: "Karate Training Santiniketan", href: "/personal-training" },
              ].map((tag, i) => (
                <Link key={i} to={tag.href} className="text-[10px] font-heading uppercase tracking-wider text-zinc-500 bg-white/5 px-3 py-1.5 rounded-full hover:bg-gg-red/10 hover:text-gg-red transition-all duration-300 btn-press">{tag.label}</Link>
              ))}
            </div>
            <a href="https://maps.google.com/?q=Bolpur+Santiniketan+731204" target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center justify-center gap-2 text-zinc-400 text-xs hover:text-gg-red transition-colors duration-300 group">
              <MapPin size={14} className="text-gg-red group-hover:scale-110 transition-transform" />
              <span className="underline underline-offset-2 decoration-zinc-700 group-hover:decoration-gg-red transition-colors">Bolpur, Santiniketan, West Bengal 731204, India</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
